import {
  ScheduleConfigurationFormData,
  scheduleTypeOptions,
} from './scheduleConfigTypes';

export function getScheduleDateInputMin(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatTime24hTo12h(time24h: string): string {
  if (!time24h) return '';
  const [hours, minutes] = time24h.split(':');
  if (!hours || !minutes) return '';
  const hour24 = Number.parseInt(hours, 10);
  if (Number.isNaN(hour24)) return '';
  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
  const ampm = hour24 >= 12 ? 'pm' : 'am';
  return `${hour12}:${minutes}${ampm}`;
}

function formatDateReadable(dateStr: string): string {
  if (!dateStr) return '';
  const dateObj = new Date(`${dateStr}T00:00:00`);
  return dateObj.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function generateRatePreviewText(data: ScheduleConfigurationFormData): string {
  const unit = data.rateUnit || 'hours';
  const value = data.rateValue || 1;
  let text = `Will execute every ${value} ${unit}`;
  if (data.rateStartDate) {
    text += ` starting ${formatDateReadable(data.rateStartDate)}`;
  }
  if (data.rateEndCondition === 'specific_date' && data.rateEndDate) {
    text += ` until ${formatDateReadable(data.rateEndDate)}`;
  }
  return text;
}

function generateOneTimePreviewText(data: ScheduleConfigurationFormData): string {
  const date = data.oneTimeDate;
  const time = data.oneTimeTime;
  const timezone = data.timezone || 'UTC';
  if (!date || !time) {
    return 'Will execute once on selected date and time';
  }
  return `Will execute once on ${formatDateReadable(date)} at ${formatTime24hTo12h(time)} (${timezone})`;
}

function generateRecurringPreviewText(data: ScheduleConfigurationFormData): string {
  const freq = data.recurringFrequency;
  const startTime = data.recurringStartTime;
  const timezone = data.timezone || 'UTC';
  if (!startTime) {
    return 'Will execute based on the recurring schedule';
  }
  const formattedTime = formatTime24hTo12h(startTime);
  const formattedDate = data.recurringStartDate
    ? formatDateReadable(data.recurringStartDate)
    : '';
  const starting = formattedDate ? ` starting ${formattedDate}` : '';
  const repeatEvery = data.recurringRepeatEvery || 1;

  if (freq === 'DAILY') {
    const dayText = repeatEvery === 1 ? 'day' : `${repeatEvery} days`;
    return `Every ${dayText} at ${formattedTime}${starting} (${timezone})`;
  }
  if (freq === 'WEEKLY') {
    const days = (data.recurringDays || []).join(', ') || 'selected days';
    return `Every ${days} at ${formattedTime}${starting} (${timezone})`;
  }
  if (freq === 'MONTHLY') {
    const dayOfMonth = data.recurringDayOfMonth;
    const dayText =
      dayOfMonth === 'l'
        ? 'last day'
        : dayOfMonth === 1
          ? 'first day'
          : dayOfMonth === 15
            ? 'middle of month'
            : `day ${dayOfMonth}`;
    const monthText = repeatEvery === 1 ? 'month' : `${repeatEvery} months`;
    return `Every ${monthText} on the ${dayText} at ${formattedTime} (${timezone})`;
  }
  return 'Will execute based on the recurring schedule';
}

export function generateSchedulePreviewText(data: ScheduleConfigurationFormData): string {
  if (data.schedule_type === scheduleTypeOptions.RATE) {
    return generateRatePreviewText(data);
  }
  if (data.schedule_type === scheduleTypeOptions.Immediate) {
    return 'You will be notified whenever there is a change happens.';
  }
  if (data.schedule_type === scheduleTypeOptions.One_time) {
    return generateOneTimePreviewText(data);
  }
  if (data.schedule_type === scheduleTypeOptions.Recurring) {
    return generateRecurringPreviewText(data);
  }
  return '';
}
